import { Component, inject } from '@angular/core';
import { BackendApi } from '../../services/backend-api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgGridAngular } from "ag-grid-angular";
import type { AutoSizeStrategy, ColDef, GetRowIdParams, GridApi, GridReadyEvent } from "ag-grid-community";
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { EXCEPTION_TYPES, UsageRate } from '../../models/usage-rate';
import { CreateUsageRateDialog } from '../create-usage-rate-dialog/create-usage-rate-dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { IconButtonCellRenderer } from '../icon-button-cell-renderer/icon-button-cell-renderer';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { UpdateUsageRateDialog } from '../update-usage-rate-dialog/update-usage-rate-dialog';

@Component({
  selector: 'app-list-usage-rates',
  imports: [AgGridAngular, MatButtonModule, MatIconModule],
  templateUrl: './list-usage-rates.html',
  styleUrl: './list-usage-rates.css',
})
export class ListUsageRates {
  constructor(
    private readonly backendApi: BackendApi,
    private readonly dialog: MatDialog
  ) { }

  private _snackBar = inject(MatSnackBar);
  private _router = inject(Router);

  public autoSizeStrategy: AutoSizeStrategy = {
    type: 'fitGridWidth',
    defaultMinWidth: 100,
  };

  public columns: ColDef<UsageRate>[] = [
    { field: "name", headerName: "Nom", filter: true, floatingFilter: true },
    { field: "description", headerName: "Description", filter: true, floatingFilter: true, sortable: false },
    { field: "is_landline", headerName: "Type", filter: true, floatingFilter: true, cellDataType: false, valueFormatter: (params) => params.value ? 'Fixe' : 'Mobile' },
    { field: "is_exception", headerName: "Grille d'exception", filter: true, floatingFilter: true },
    { field: "type", headerName: "Type d'exception", filter: true, floatingFilter: true, valueFormatter: (params) => {
      if (!params.data?.is_exception) {
        return '';
      }

      if (params.value == null) {
        return 'Non specifié';
      }
      
      if (params.value in EXCEPTION_TYPES) {
        return EXCEPTION_TYPES[params.value];
      }

      return '';
    } },
    { field: "is_default", headerName: "Grille par défaut", filter: true, floatingFilter: true },
    { field: "service", headerName: "Service", filter: true, floatingFilter: true },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'edit',
        tooltip: 'Modifier cette grille de vente',
        onClick: (data: any, index: number) => this.updateUsageRate(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'delete',
        tooltip: 'Supprimer cette grille de vente',
        onClick: (data: any, index: number) => this.deleteUsageRate(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    }
  ];

  public localeText = AG_GRID_LOCALE_FR;

  private gridApi!: GridApi;

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;

    this.backendApi.getUsageRates().subscribe({
      next: (usageRates) => {
        event.api.setGridOption('loading', false);
        event.api.setGridOption('rowData', usageRates);
      },
      error: () => {
        // Error encountered
        event.api.setGridOption('loading', false);
        this._snackBar.open("Erreur rencontrée lors de la recuperation des grilles.");
      }
    })
  }

  getRowId(params: GetRowIdParams): string {
    return params.data.uuid;
  }

  selectUsageRate(uuid?: string) {
    if (!uuid) {
      return;
    }

    this._router.navigate(["/usageRates", uuid]);
  }

  

  createUsageRate() {
      const dialogRef = this.dialog.open(CreateUsageRateDialog, {
        width: '400px'
      });
  
      dialogRef.afterClosed().subscribe((newPackage) => {
        if (newPackage) {
          this.gridApi.applyTransaction({
            add: [newPackage]
          });
        }
      });
  }

  updateUsageRate(usageRate: any, index: number): void {
    const dialogRef = this.dialog.open(UpdateUsageRateDialog, {
      width: '400px',
      data: usageRate
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.gridApi.applyTransaction({update: [result]});
    });
  }

  deleteUsageRate(usageRate: any, index: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer cette grille de vente ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.backendApi.deleteUsageRate(usageRate.uuid).subscribe({
        next: () => {
          this.gridApi.applyTransaction({remove: [usageRate]});
          this._snackBar.open("Grille de vente supprimée.");
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }
}
